/**
 * Application route constants
 *
 * Centralized route definitions for type-safe navigation
 */

/**
 * Marketing routes (public, no auth required)
 *
 * Sprint 041: Marketing pages consolidated into Frontend app.
 * These replace the separate Marketing SWA site.
 */
export const MARKETING_ROUTES = {
  HOME: '/',
  HOW_IT_WORKS: '/how-it-works',
  BENEFITS: '/benefits',
  FEATURES: '/features',
  EXAMPLES: '/examples',
  CONTACT: '/contact',
  SOCIAL_IMPACT: '/social-impact',
} as const;

/**
 * Public routes (authentication and legal pages)
 *
 * Sprint 041: Login, register, and password flows will be re-activated
 * in Session 3 when auth pages are migrated from Marketing site.
 */
export const PUBLIC_ROUTES = {
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  // SESSION_189: Email confirmation routes
  CONFIRM_EMAIL_PENDING: '/confirm-email-pending',  // Shown after registration
  EMAIL_CONFIRMED: '/email-confirmed',              // Redirect target after clicking email link
  // SESSION_210: Legal pages
  PRIVACY: '/privacy',
  TERMS: '/terms',
  // Sprint 046 Session 9: Unsubscribe page
  UNSUBSCRIBE: '/unsubscribe',
  // Sprint 062 Session 7: Non-profit invite acceptance
  NONPROFIT_INVITE: '/nonprofit-invite/:token',
} as const;

/**
 * Customer routes (customer role required)
 * Sprint 037 Session 9: Removed DASHBOARD - MentionsPage is now the primary landing page
 */
export const CUSTOMER_ROUTES = {
  /** @deprecated Use MENTIONS instead - Dashboard has been consolidated into MentionsPage */
  DASHBOARD: '/mentions',      // Redirects to mentions for backwards compatibility
  MENTIONS: '/mentions',       // Primary landing page for business owners
  MENTION_DETAIL: '/mentions/:id',
  MY_BUSINESS: '/my-business',
  MY_BUSINESS_ALIASES: '/my-business/aliases',  // SESSION_220: Alias management
  MY_FRANCHISE: '/my-franchise',                // SESSION_221: Franchise management for company owners
  BILLING: '/billing',
  SETTINGS: '/settings',       // SESSION_201: Settings page
  ONBOARDING: '/onboarding'    // SESSION_199: Onboarding flow
} as const;

/**
 * Non-profit user routes (NonProfitUser role required)
 * Sprint 062 Session 7
 */
export const NONPROFIT_ROUTES = {
  DASHBOARD: '/nonprofit/dashboard',
} as const;

/**
 * Admin routes (admin role required)
 */
export const ADMIN_ROUTES = {
  DASHBOARD: '/admin',
  ANALYTICS: '/admin/analytics',
  REGIONS: '/admin/regions',
  REGION_DETAIL: '/admin/regions/:id',
  CREATE_REGION: '/admin/regions/create',
  USERS: '/admin/users',
  USER_DETAIL: '/admin/users/:id',
  CREATE_USER: '/admin/users/create',
  BUSINESSES: '/admin/businesses',
  BUSINESS_DETAIL: '/admin/businesses/:id',
  CREATE_BUSINESS: '/admin/businesses/create',
  COMPANIES: '/admin/companies',
  COMPANY_SUGGESTIONS: '/admin/companies/suggestions',
  COMPANY_DETAIL: '/admin/companies/:id',
  MENTIONS: '/admin/mentions',
  MENTION_DETAIL: '/admin/mentions/:id',
  MENTION_REVIEW: '/admin/mentions/review',
  MENTION_REVIEW_DETAIL: '/admin/mentions/review/:id',
  POST_REVIEW: '/admin/posts/review',
  POST_REVIEW_DETAIL: '/admin/posts/review/:postId',
  IMPORTS: '/admin/imports',
  ADDRESS_CORRECTIONS: '/admin/address-corrections',
  ALIAS_REVIEW: '/admin/alias-review',  // SESSION_218: Alias Review Queue (legacy, redirect to ADMIN_REVIEW)
  ADMIN_REVIEW: '/admin/review',  // SESSION_312: Unified Admin Review Queue
  COMPANY_CLAIM_REVIEW: '/admin/company-claims',  // SESSION_221: Company Claim Review
  LOCATION_REQUEST_REVIEW: '/admin/location-requests',  // SESSION_221: Location Request Review
  // Sprint 062: Non-Profit Management
  NONPROFITS: '/admin/nonprofits',
  NONPROFIT_DETAIL: '/admin/nonprofits/:id',
  NONPROFIT_PHRASE_REQUESTS: '/admin/nonprofits/phrase-requests',
  NONPROFIT_MENTIONS: '/admin/nonprofits/mentions',
  // SESSION_257: CRM Routes
  CRM_DASHBOARD: '/admin/crm',
  CRM_CONTACTS: '/admin/crm/contacts',
  CRM_CONTACT_DETAIL: '/admin/crm/contacts/:id',
  CRM_EMAIL_ANALYTICS: '/admin/crm/email-analytics',
  // Sprint 065: Registration Funnel Viewer
  FUNNEL: '/admin/funnel'
} as const;

/**
 * Error/utility routes
 */
export const UTILITY_ROUTES = {
  ACCESS_DENIED: '/access-denied',
  NOT_FOUND: '/404'
} as const;

/**
 * All routes combined
 */
export const ROUTES = {
  ...MARKETING_ROUTES,
  ...PUBLIC_ROUTES,
  ...CUSTOMER_ROUTES,
  ...NONPROFIT_ROUTES,
  ...ADMIN_ROUTES,
  ...UTILITY_ROUTES
} as const;

/**
 * Helper to build route with parameters
 */
export function buildRoute(route: string, params: Record<string, string>): string {
  let builtRoute = route;

  Object.entries(params).forEach(([key, value]) => {
    builtRoute = builtRoute.replace(`:${key}`, value);
  });

  return builtRoute;
}

/**
 * Helper to build mention detail route
 */
export function buildMentionDetailRoute(id: string, isAdmin = false): string {
  return isAdmin
    ? buildRoute(ADMIN_ROUTES.MENTION_DETAIL, { id })
    : buildRoute(CUSTOMER_ROUTES.MENTION_DETAIL, { id });
}

/**
 * Helper to build business detail route
 */
export function buildBusinessDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.BUSINESS_DETAIL, { id });
}

/**
 * Helper to build region detail route
 */
export function buildRegionDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.REGION_DETAIL, { id });
}

/**
 * Helper to build user detail route
 */
export function buildUserDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.USER_DETAIL, { id });
}

/**
 * Helper to build post review detail route
 */
export function buildPostReviewDetailRoute(postId: string): string {
  return buildRoute(ADMIN_ROUTES.POST_REVIEW_DETAIL, { postId });
}

/**
 * Helper to build mention review detail route
 */
export function buildMentionReviewDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.MENTION_REVIEW_DETAIL, { id });
}

/**
 * Helper to build company detail route
 */
export function buildCompanyDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.COMPANY_DETAIL, { id });
}

/**
 * Helper to build CRM contact detail route
 */
export function buildContactDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.CRM_CONTACT_DETAIL, { id });
}

/**
 * Helper to build non-profit detail route
 */
export function buildNonProfitDetailRoute(id: string): string {
  return buildRoute(ADMIN_ROUTES.NONPROFIT_DETAIL, { id });
}
